import React, { useState, useRef, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { FormattedMessage } from 'react-intl'
import { detect } from 'detect-browser'
import { useNavigate } from 'react-router-dom'

import AppStore from '../../../stores/AppStore'
import Footer from '../../partials/Footer'
import { useAuth } from '../../../util/AuthContext'

import LogoLarge from '../../partials/Graphics/LogoLarge'
import LoginErrorModal from '../../partials/LoginErrorModal'
import {
  Page,
  Card,
  LogoWrap,
  CardTitle,
  CardText,
  Field,
  FieldLabel,
  FieldInput,
  FieldError,
  PrimaryButton,
  LinkButton,
  SuccessBox,
  Warning,
  Terms,
} from './loginStyles'
import AppDispatcher from '../../../dispatchers/AppDispatcher.js'
import getBrandName from '../../../util/getBrandName'

const browser = detect()

const LOGIN_VIEW = 'login'
const FORGOT_VIEW = 'forgot'
const FORGOT_SENT_VIEW = 'forgot_sent'
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Login page functional component.
 */
const Login = () => {
  const emailInput = useRef()
  const passwordInput = useRef()
  const passwordResetEmailInput = useRef()
  const navigate = useNavigate()
  const { auth, login, isAuthenticated } = useAuth()

  const [state, setState] = useState({
    atFront: LOGIN_VIEW,
    brand: {},
    email: '',
  })

  const [forgotError, setForgotError] = useState('')
  const [linkResent, setLinkResent] = useState(false)

  const [loginError, setLoginError] = useState({
    visible: false,
    message: '',
  })

  useEffect(() => {
    // initialize Brand based on window location
    AppDispatcher.dispatch({ action: 'get-brand', name: getBrandName() })
  }, [])

  // Effect for initial setup and cleanup
  useEffect(() => {
    AppStore.addChangeListener(onChange)
    setBrand()

    if (isAuthenticated()) {
      navigate('/home')
    }

    return () => {
      try {
        AppStore.removeChangeListener(onChange)
      } catch (error) {
        console.error(error)
      }
    }
  }, [isAuthenticated, navigate])

  // Effect to check authentication on changes
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/home')
    }
  }, [isAuthenticated, navigate])

  const onChange = () => {
    setBrand()
  }

  const setBrand = () => {
    setState(prevState => ({ ...prevState, brand: AppStore.getCustomerBrand() }))
  }

  const handleLogin = async () => {
    const email = emailInput.current.value.trim()
    const password = passwordInput.current.value

    if (!email || !password) {
      setLoginError({
        visible: true,
        message: 'Please enter both email and password.',
      })
      return
    }

    try {
      const success = await login(email, password)
      if (!success) {
        setLoginError({
          visible: true,
          message: 'Invalid email or password. Please try again.',
        })
      }
    } catch (error) {
      setLoginError({
        visible: true,
        message: 'Login failed. Please check your credentials and try again.',
      })
    }
  }

  const handleForgot = () => {
    setForgotError('')
    setState(prevState => ({ ...prevState, atFront: FORGOT_VIEW }))
  }

  const handleBackToLogin = () => {
    setForgotError('')
    setState(prevState => ({ ...prevState, atFront: LOGIN_VIEW }))
  }

  const handlePasswordReset = () => {
    const email = passwordResetEmailInput.current.value.trim()
    if (!email) {
      setForgotError('required')
      return
    }
    if (!EMAIL_REGEX.test(email)) {
      setForgotError('invalid')
      return
    }
    setForgotError('')
    setLinkResent(false)
    auth.passwordRecovery({ email })
    setState(prevState => ({ ...prevState, atFront: FORGOT_SENT_VIEW, email }))
  }

  const handleResendLink = () => {
    const { email } = state
    auth.passwordRecovery({ email })
    setLinkResent(true)
  }

  const closeErrorModal = () => {
    setLoginError({
      visible: false,
      message: '',
    })
  }

  const renderLogin = () => {
    let browserWarning
    switch (browser && browser.name) {
      case 'chrome':
      case 'crios':
      case 'firefox':
      case 'safari':
      case 'ios':
        break
      default:
        browserWarning = (
          <Warning>
            <FormattedMessage
              id='landing.browserWarning'
              defaultMessage='Warning: Your browser {browserName} may not be 100% compatible with our website. Please consider switching to a recent version of Chrome, Safari or Firefox Desktop before attempting to login.'
              description='Browser Warning'
              values={{
                browserName: ((browser && browser.name) || '').toUpperCase(),
              }}
            />
          </Warning>
        )
    }

    const {
      brand: { banner_svg: bannerSVG },
    } = state

    return (
      <Page id='login-page'>
        {browserWarning}
        <Card>
          <LogoWrap>
            <LogoLarge func={handleBackToLogin} icon={bannerSVG} color='#284047' />
          </LogoWrap>
          <form id='login' onSubmit={(e) => { e.preventDefault(); handleLogin() }} noValidate>
            <Field>
              <FieldLabel htmlFor='email'>
                <FormattedMessage
                  id='landing.email'
                  defaultMessage='Email'
                  description='Email for login'
                />
              </FieldLabel>
              <FieldInput
                ref={emailInput}
                type='email'
                name='email'
                id='email'
                autoComplete='username'
              />
            </Field>
            <Field>
              <FieldLabel htmlFor='password'>
                <FormattedMessage
                  id='landing.password'
                  defaultMessage='Password'
                  description='Password for login'
                />
              </FieldLabel>
              <FieldInput
                ref={passwordInput}
                type='password'
                name='password'
                id='password'
                autoComplete='current-password'
              />
            </Field>
            <PrimaryButton type='submit'>
              <FormattedMessage
                id='landing.login'
                defaultMessage='LOGIN'
                description='Login to the application'
              />
            </PrimaryButton>
          </form>
          <LinkButton type='button' onClick={handleForgot}>
            <FormattedMessage
              id='landing.forgotPassword'
              defaultMessage='Forgot your password?'
              description='Forgot password text'
            />
          </LinkButton>
          <Terms>
            <a href='https://altrac.io/pages/terms-of-service'>
              <FormattedMessage
                id='landing.agreeToTerms'
                defaultMessage='By logging in, You agree to Our Terms Of Use. Click here to read them.'
                description='Agree to terms'
                tagName='h6'
              />
            </a>
          </Terms>
        </Card>
        <Footer color='white' />
      </Page>
    )
  }

  const renderForgot = () => (
    <Page>
      <Card>
        <LogoWrap>
          <LogoLarge func={handleBackToLogin} icon={state.brand.banner_svg} color='#284047' />
        </LogoWrap>
        <CardTitle>
          <FormattedMessage
            id='resetPassword.reset'
            defaultMessage='Reset Password'
            description='Reset password title'
          />
        </CardTitle>
        <CardText>
          <FormattedMessage
            id='resetPassword.toReset'
            defaultMessage='To reset your password, please enter the email associated with your Altrac account below'
            description='Reset password instructions'
          />
        </CardText>
        <form onSubmit={(e) => { e.preventDefault(); handlePasswordReset() }} noValidate>
          <Field>
            <FieldLabel htmlFor='reset-email'>
              <FormattedMessage
                id='landing.email'
                defaultMessage='Email'
                description='Email for login'
              />
            </FieldLabel>
            <FieldInput
              ref={passwordResetEmailInput}
              type='email'
              name='email'
              id='reset-email'
              autoComplete='email'
              $error={!!forgotError}
              aria-invalid={!!forgotError}
              onChange={() => forgotError && setForgotError('')}
            />
            {forgotError === 'required' && (
              <FieldError role='alert'>
                <FormattedMessage
                  id='resetPassword.emailRequired'
                  defaultMessage='Please enter your email address.'
                />
              </FieldError>
            )}
            {forgotError === 'invalid' && (
              <FieldError role='alert'>
                <FormattedMessage
                  id='resetPassword.emailInvalid'
                  defaultMessage='Please enter a valid email address.'
                />
              </FieldError>
            )}
          </Field>
          <PrimaryButton type='submit'>
            <FormattedMessage
              id='resetPassword.next'
              defaultMessage='NEXT'
              description='Reset password next button'
            />
          </PrimaryButton>
        </form>
        <LinkButton type='button' onClick={handleBackToLogin}>
          <FormattedMessage
            id='resetPassword.back'
            defaultMessage='BACK'
            description='Reset password back button'
          />
        </LinkButton>
      </Card>
      <Footer color='white' />
    </Page>
  )

  const renderForgotSent = (email) => (
    <Page>
      <Card>
        <LogoWrap>
          <LogoLarge func={handleBackToLogin} icon={state.brand.banner_svg} color='#284047' />
        </LogoWrap>
        <CardTitle>
          <FormattedMessage
            id='resetPassword.reset'
            defaultMessage='Reset Password'
            description='Reset password title'
          />
        </CardTitle>
        <SuccessBox role='status'>
          <FormattedMessage
            id='resetPassword.emailMessage'
            defaultMessage='An email with a link to reset your password has been sent to: {email}'
            description='Reset password message'
            values={{
              email,
            }}
          />
        </SuccessBox>
        <CardText>
          <FormattedMessage
            id='resetPassword.emailNotReceived'
            defaultMessage='If you have not received the email with 10 minutes - click the button below to resend.'
            description='Reset password resend message'
          />
        </CardText>
        {linkResent && (
          <SuccessBox role='status'>
            <FormattedMessage
              id='resetPassword.linkResent'
              defaultMessage='A new link has been sent to {email}.'
              values={{ email }}
            />
          </SuccessBox>
        )}
        <PrimaryButton type='button' onClick={handleResendLink}>
          <FormattedMessage
            id='resetPassword.emailResend'
            defaultMessage='RESEND LINK'
            description='Resend link button'
          />
        </PrimaryButton>
        <LinkButton type='button' onClick={handleBackToLogin}>
          <FormattedMessage
            id='resetPassword.back'
            defaultMessage='BACK'
            description='Reset password back button'
          />
        </LinkButton>
      </Card>
      <Footer color='white' />
    </Page>
  )

  const {
    atFront,
    brand: { display_name: title = '' } = {},
    email,
  } = state

  return (
    <>
      <Helmet><title>{title}</title></Helmet>
      <div>
        {atFront === LOGIN_VIEW && renderLogin()}
        {atFront === FORGOT_VIEW && renderForgot()}
        {atFront === FORGOT_SENT_VIEW && renderForgotSent(email)}
        <LoginErrorModal
          visible={loginError.visible}
          onClose={closeErrorModal}
          errorMessage={loginError.message}
        />
      </div>
    </>
  )
}

export default Login
